import { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { Loader2 } from 'lucide-react';
import useAdmin from '../hooks/useAdmin';
import { useConfirmModal } from '../utils/modal'; // Import useConfirmModal
import ConfirmModal from '../../shared/components/ConfirmModal';

interface Request {
  user_id: string;
  email: string;
  request_date: string;
}

export default function VerificationRequests() {
  const [requests, setRequests] = useState<Request[]>([]);
  const [loading, setLoading] = useState(false);
  const { token, fetchVerificationRequests, verifyUser, error, setError, socket } = useAdmin();
  const { isOpen, message, showConfirmModal, handleConfirm, handleCancel } = useConfirmModal(); // Use useConfirmModal

  useEffect(() => {
    if (token) {
      setLoading(true);
      fetchVerificationRequests().then(data => {
        setRequests(data);
        setLoading(false);
      });
    }
  }, [token, fetchVerificationRequests]);

  useEffect(() => {
    if (socket) {
      socket.on('new-verification-request', () => fetchVerificationRequests().then(setRequests));
      socket.on('verification-updated', () => fetchVerificationRequests().then(setRequests));
      return () => {
        socket.off('new-verification-request');
        socket.off('verification-updated');
      };
    }
  }, [socket, fetchVerificationRequests]);

  const handleVerification = (userId: string, approve: boolean) => {
    showConfirmModal(
      `Are you sure you want to ${approve ? 'approve' : 'reject'} this request?`,
      () => verifyUser(userId, approve).then(() => fetchVerificationRequests().then(setRequests))
    );
  };

  return (
    <div className="p-4">
      <h2 className="text-2xl font-bold mb-4">Real Money Approval Requests</h2>
      <div className="bg-white p-4 rounded-lg shadow">
        {error && (
          <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-lg flex justify-between items-center">
            {error}
            <button onClick={() => setError('')} className="underline" aria-label="Dismiss error">
              Dismiss
            </button>
          </div>
        )}
        {loading ? (
          <div className="flex items-center justify-center">
            <Loader2 className="animate-spin" size={48} />
            <span className="ml-4 text-xl">Loading...</span>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-100">
                  <th className="p-2 text-left">Email</th>
                  <th className="p-2 text-left">Request Date</th>
                  <th className="p-2 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {requests.map(req => (
                  <tr key={req.user_id} className="border-b hover:bg-gray-50">
                    <td className="p-2" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(req.email || 'N/A') }} />
                    <td className="p-2">{req.request_date ? new Date(req.request_date).toLocaleString() : 'N/A'}</td>
                    <td className="p-2 space-x-2">
                      <button
                        className="px-2 py-1 bg-green-500 text-white rounded-lg hover:bg-green-600 transition"
                        onClick={() => handleVerification(req.user_id, true)}
                        aria-label={`Approve verification request for ${req.email}`}
                      >
                        Approve
                      </button>
                      <button
                        className="px-2 py-1 bg-red-500 text-white rounded-lg hover:bg-red-600 transition"
                        onClick={() => handleVerification(req.user_id, false)}
                        aria-label={`Reject verification request for ${req.email}`}
                      >
                        Reject
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <ConfirmModal
          isOpen={isOpen}
          message={message}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      </div>
    </div>
  );
}